document.addEventListener("DOMContentLoaded", () => {
  const containers = document.querySelectorAll(
    ".container-discover-our-collection"
  );

  containers.forEach((container) => {
    const carousel = container.querySelector(".carousel-wrapper");
    const carouselContainer = container.querySelector(".carousel-container");
    const leftArrow = container.querySelector(".arrow.left");
    const rightArrow = container.querySelector(".arrow.right");
    const cards = Array.from(carousel?.querySelectorAll(".card-wrapper") || []);
    const instructions = container.querySelector(".carousel-instructions");

    if (!carousel || !leftArrow || !rightArrow || !cards.length) return;

    let io = null;
    let dragState = null;
    let suppressLinkClickUntil = 0;
    const dragThreshold = 6;

    const hasOverflow = () => carousel.scrollWidth > carousel.clientWidth + 1;

    const getScrollAmount = () => {
      const box = container.querySelector(".box");
      if (!box) return 0;
      const gap = parseInt(getComputedStyle(carousel).gap || 0, 10);
      return box.offsetWidth + gap;
    };

    const getVisibilityRatio = (card) => {
      const rootRect = carousel.getBoundingClientRect();
      const rect = card.getBoundingClientRect();
      const overlap =
        Math.min(rect.right, rootRect.right) -
        Math.max(rect.left, rootRect.left);
      const visibleWidth = Math.max(0, overlap);
      return rect.width ? visibleWidth / rect.width : 0;
    };

    const updateArrowsVisibility = () => {
      const scrollLeft = carousel.scrollLeft;
      const maxScrollLeft = carousel.scrollWidth - carousel.clientWidth;

      const lastCard = cards[cards.length - 1];
      const lastCardVisible = lastCard ? getVisibilityRatio(lastCard) >= 0.2 : false;

      const atStart = scrollLeft <= 0;
      const atEnd = scrollLeft >= maxScrollLeft - 1 || lastCardVisible;

      if (atStart) {
        leftArrow.classList.add("hidden");
        leftArrow.setAttribute("tabindex", "-1");
      } else {
        leftArrow.classList.remove("hidden");
        leftArrow.setAttribute("tabindex", "0");
      }

      if (atEnd) {
        rightArrow.classList.add("hidden");
        rightArrow.setAttribute("tabindex", "-1");
      } else {
        rightArrow.classList.remove("hidden");
        rightArrow.setAttribute("tabindex", "0");
      }
    };

    const getVisibleLinks = () => {
      return cards
        .map((card) => {
          const link = card.querySelector("a");
          if (!link) return null;

          if (!hasOverflow()) {
            return { card, link, ratio: 1 };
          }

          const ratio = getVisibilityRatio(card);

          return ratio >= 0.2 ? { card, link, ratio } : null;
        })
        .filter(Boolean);
    };

    const updateTabbableCards = () => {
      const visible = getVisibleLinks();

      cards.forEach((card) => {
        const link = card.querySelector("a");
        if (!link) return;

        const isVisible = visible.some((v) => v.card === card);
        if (isVisible) {
          link.removeAttribute("tabindex");
        } else {
          link.setAttribute("tabindex", "-1");
        }
      });
    };

    const focusVisibleCard = ({ last = false } = {}) => {
      const visible = getVisibleLinks();
      if (!visible.length) return;
      const target = last ? visible[visible.length - 1] : visible[0];
      target?.link?.focus();
    };

    const markVisibility = (entry) => {
      const card = entry.target;
      const link = card.querySelector("a");
      const mostlyVisible =
        entry.isIntersecting && entry.intersectionRatio >= 0.2;

      if (!link) return;

      if (mostlyVisible) {
        link.removeAttribute("tabindex");
      } else {
        link.setAttribute("tabindex", "-1");
      }
    };

    const startObserver = () => {
      if (io) return;
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach(markVisibility);
        },
        {
          root: carousel,
          threshold: [0, 0.5, 0.99, 1],
        }
      );
      cards.forEach((c) => io.observe(c));
      requestAnimationFrame(() => updateArrowsVisibility());
    };

    const stopObserverAndReset = () => {
      if (io) {
        io.disconnect();
        io = null;
      }

      cards.forEach((card) => {
        const link = card.querySelector("a");
        if (link) link.removeAttribute("tabindex");
      });
    };

    const enableCarouselMode = () => {
      leftArrow.classList.remove("hidden");
      rightArrow.classList.remove("hidden");
      instructions.style.display = "block";
      startObserver();
      updateArrowsVisibility();
      updateTabbableCards();
    };

    const disableCarouselMode = () => {
      leftArrow.classList.add("hidden");
      rightArrow.classList.add("hidden");
      instructions.style.display = "none";
      stopObserverAndReset();
      updateTabbableCards();
    };

    const syncMode = () => {
      if (hasOverflow()) {
        const label = carouselContainer.dataset.carouselLabel;
        carouselContainer.setAttribute("aria-label", label);
        enableCarouselMode();

      } else {
        carouselContainer.removeAttribute("aria-label");
        disableCarouselMode();
      }
    };

    leftArrow.addEventListener("click", () => {
      const step = getScrollAmount();
      carousel.scrollBy({ left: -step, behavior: "smooth" });
    });

    const handleAdvanceFocus = () => {
      // If user is interacting with keyboard (arrow is the active element),
      // move focus to the newly visible card after the scroll finishes.
      const triggeredFromKeyboard = document.activeElement === rightArrow;
      if (!triggeredFromKeyboard) return;

      requestAnimationFrame(() => {
        setTimeout(() => {
          updateTabbableCards();
          focusVisibleCard({ last: true });
        }, 160);
      });
    };

    rightArrow.addEventListener("click", () => {
      const step = getScrollAmount();
      carousel.scrollBy({ left: step, behavior: "smooth" });
      handleAdvanceFocus();
    });

    const stopDragging = (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;

      const { didDrag, pointerId } = dragState;
      dragState = null;
      carousel.classList.remove("is-dragging");

      if (carousel.hasPointerCapture(pointerId)) {
        carousel.releasePointerCapture(pointerId);
      }

      if (didDrag) {
        // Prevent a drag that started on a CTA from activating its link on release.
        suppressLinkClickUntil = Date.now() + 250;
      }

      updateArrowsVisibility();
      updateTabbableCards();
    };

    carousel.addEventListener("pointerdown", (event) => {
      if (
        event.pointerType !== "mouse" ||
        event.button !== 0 ||
        !hasOverflow()
      ) {
        return;
      }

      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: carousel.scrollLeft,
        didDrag: false,
      };
      carousel.setPointerCapture(event.pointerId);
    });

    carousel.addEventListener("pointermove", (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;

      const deltaX = event.clientX - dragState.startX;
      if (!dragState.didDrag && Math.abs(deltaX) < dragThreshold) return;

      dragState.didDrag = true;
      carousel.classList.add("is-dragging");
      carousel.scrollLeft = dragState.startScrollLeft - deltaX;
      event.preventDefault();
    });

    carousel.addEventListener("pointerup", stopDragging);
    carousel.addEventListener("pointercancel", stopDragging);
    carousel.addEventListener("lostpointercapture", stopDragging);

    carousel.addEventListener(
      "click",
      (event) => {
        if (
          Date.now() > suppressLinkClickUntil ||
          !event.target.closest("a")
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        suppressLinkClickUntil = 0;
      },
      true
    );

    carousel.addEventListener("scroll", () => {
      if (hasOverflow()) updateArrowsVisibility();
      updateTabbableCards();
    });

    carousel.addEventListener("focusin", (ev) => {
      if (!hasOverflow()) return;
      const link = ev.target.closest("a");
      if (!link) return;
      const card = link.closest(".card-wrapper");
      if (!card) return;

      const cRect = carousel.getBoundingClientRect();
      const iRect = card.getBoundingClientRect();

      if (iRect.left < cRect.left) {
        carousel.scrollBy({
          left: iRect.left - cRect.left,
          behavior: "smooth",
        });
      } else if (iRect.right > cRect.right) {
        carousel.scrollBy({
          left: iRect.right - cRect.right,
          behavior: "smooth",
        });
      }
    });

    const ro = new ResizeObserver(syncMode);
    ro.observe(carousel);
    window.addEventListener("orientationchange", syncMode);

    syncMode();
    updateTabbableCards();
  });
});
